import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import SocketService from "../services/SocketService";
import FindingLawyer, { SEARCH_SECONDS } from "../components/call/FindingLawyer";
import CallScreen from "../components/call/CallScreen";
import { ensureMediaPermission, getMediaPermissionState } from "../components/call/media";
import MediaPermissionModal from "../components/call/MediaPermissionModal";
import lawyerImg from "../assets/images/hireNow.png";

const STRIPE_JS = "https://js.stripe.com/v3/";

// The OTP-verified auth token is persisted here so it survives the checkout
// reset after payment and is still available for the endCall API. It is removed
// once the call ends.
const AUTH_TOKEN_KEY = "cc_auth_token";

// Load the Stripe.js script once and resolve when window.Stripe is available.
const loadStripeJs = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    let script = document.querySelector(`script[src="${STRIPE_JS}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = STRIPE_JS;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve(window.Stripe));
    script.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
  });

const HOW_IT_WORKS = [
  { icon: "bi-clipboard-check", title: "Provide Details", text: "Select category, jurisdiction, describe your issue and choose duration." },
  { icon: "bi-person-vcard", title: "We Find a Lawyer", text: "We match you with the most suitable and available lawyer." },
  { icon: "bi-camera-video", title: "Connect Instantly", text: "Get connected via secure video call in under 60 seconds." },
  { icon: "bi-chat-dots", title: "Get Legal Help", text: "Discuss your case and get expert legal advice." },
];

const ConsultationCall = () => {
  // Form state
  const [categoriesList, setCategoriesList] = useState([]);
  const [jurisdictionsList, setJurisdictionsList] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  // Truthy once the user has already used their free consultation: the free
  // package(s) get disabled and only paid ones are selectable. Falsy (not
  // taken yet) disables the paid packages instead, so the free trial is used
  // first.
  const [isFreeTaken, setIsFreeTaken] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  // "Write with AI" for the description field
  const [aiLoading, setAiLoading] = useState(false);
  const previousDescriptionRef = useRef("");

  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  // 1 = verify identity (name/email/OTP), 2 = choose package (fetched with
  // the OTP-verified token), 3 = payment / start call.
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  // True while the browser's camera/mic prompt is up, so the CTA can't be
  // fired again behind it.
  const [checkingMedia, setCheckingMedia] = useState(false);
  // { reason, state } while the camera/mic modal is up, null when it isn't.
  const [mediaPrompt, setMediaPrompt] = useState(null);

  // Call flow state: idle | searching | unavailable | connected
  const [callPhase, setCallPhase] = useState("idle");
  const [matchedLawyers, setMatchedLawyers] = useState([]);
  const [availableCount, setAvailableCount] = useState(0);
  const [lawyersFound, setLawyersFound] = useState(false); // initiate API returned
  const [activeCall, setActiveCall] = useState(null); // { callId, channel, creds, lawyer }

  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const clientSecretRef = useRef(null);
  const publishableKeyRef = useRef(null);
  const callRef = useRef(null);          // mirrors activeCall for socket handlers
  const phaseRef = useRef("idle");
  const matchedRef = useRef([]);         // mirrors matchedLawyers for socket handlers
  const searchTimer = useRef(null);
  callRef.current = activeCall;
  phaseRef.current = callPhase;

  // Fetches call packages using the OTP-verified auth token. Called once OTP
  // verification succeeds (step 1 -> step 2 of checkout), since the backend
  // needs the header to return token-aware pricing/eligibility. Preserves the
  // current selection across re-fetches instead of resetting it.
  const fetchCallPackages = async (token) => {
    setPackagesLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL;
      const res = await axios.get(
        `${base}/getCallPackages`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (res?.data?.status) {
        const list = res.data.data || [];
        const freeTaken = !!res.data.is_free_taken;
        const isFree = (p) => !!p.is_free || Number(p.price) === 0;
        // Only the free plan(s) are selectable before it's been used; only
        // paid plans are selectable after.
        const isSelectable = (p) => (freeTaken ? !isFree(p) : isFree(p));
        setIsFreeTaken(freeTaken);
        setPackages(list);
        setSelectedPackage((prev) => {
          const stillThere = prev && list.find((p) => String(p.id) === String(prev.id) && isSelectable(p));
          return (
            stillThere ||
            list.find((p) => isSelectable(p) && p.is_popular) ||
            list.find(isSelectable) ||
            null
          );
        });
      }
    } catch (e) {
      console.error("Error fetching call packages:", e);
    } finally {
      setPackagesLoading(false);
    }
  };

  // ---- Load dropdowns -----------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const base = process.env.REACT_APP_API_URL;
        const [cats, dropdown] = await Promise.all([
          axios.get(`${base}/getCategories`),
          axios.get(`${base}/getDropdownData`),
        ]);
        if (cats?.data?.status) setCategoriesList(cats.data.data || []);
        if (dropdown?.data?.status) setJurisdictionsList(dropdown.data.data.jurisdictions || []);
      } catch (e) {
        console.error("Error fetching consultation data:", e);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  // ---- OTP ----------------------------------------------------------------
  const handleSendOtp = async () => {
    if (!fullName || !email) {
      toast.error("Please provide both name and email.");
      return;
    }
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", fullName);
      formData.append("email", email);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/resendOTP`, formData);
      if (res?.data?.status) {
        toast.success(res?.data?.message || "OTP sent successfully!");
      } else {
        toast.error(res?.data?.message || "Failed to send OTP.");
      }
    } catch (e) {
      toast.error("An error occurred while sending OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    const verifyOtp = async () => {
      if (otp.length !== 4 || !email) return;
      try {
        const formData = new FormData();
        formData.append("email", email);
        formData.append("otp", otp);
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/verifyOTP`, formData);
        if (res?.data?.status) {
          toast.success(res?.data?.message || "OTP verified successfully!");
          setOtpVerified(true);
          setAuthToken(res.data.data.auth_token);
          localStorage.setItem(AUTH_TOKEN_KEY, res.data.data.auth_token);
          // Re-fetch with the token now available so the backend can return
          // its token-aware packages data.
          fetchCallPackages(res.data.data.auth_token);
        } else {
          toast.error(res?.data?.message || "Invalid OTP.");
          setOtpVerified(false);
          setAuthToken(null);
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } catch (e) {
        toast.error("An error occurred during OTP verification.");
        setOtpVerified(false);
        setAuthToken(null);
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    };
    verifyOtp();
  }, [otp, email]);

  // Prefer the persisted token: after payment the checkout state (incl. the
  // authToken state) is reset, but the call flow (endCall etc.) still needs it.
  const getAuthToken = () => authToken || localStorage.getItem(AUTH_TOKEN_KEY);

  // ---- AI-assisted description --------------------------------------------
  // Expands the user's short note into a full description. Calls the
  // throttled backend endpoint, which proxies to whichever provider
  // (Claude/OpenAI) the admin has configured.
  const handleAiAssist = async () => {
    if (aiLoading || !caseDescription.trim()) return;
    setAiLoading(true);
    previousDescriptionRef.current = caseDescription;
    try {
      const base = process.env.REACT_APP_API_URL;
      const res = await axios.post(`${base}/assistDescription`, {
        text: caseDescription,
        category_id: categoryId || undefined,
        jurisdiction_id: jurisdictionId || undefined,
      });
      if (res?.data?.status && res.data.data?.suggestion) {
        setCaseDescription(res.data.data.suggestion);
      } else {
        toast.error(res?.data?.message || "AI assistant could not process your text.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "AI assistant is unavailable right now.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleUndoAiAssist = () => setCaseDescription(previousDescriptionRef.current);

  // ---- Open checkout ------------------------------------------------------
  const handleStartConnecting = async () => {
    if (!categoryId || !jurisdictionId || !caseDescription.trim()) {
      toast.error("Please choose a category, jurisdiction and describe your issue.");
      return;
    }
    // Settle the camera and mic before the card, not after. Agora would not ask
    // until it joins the channel, by which point the client has been charged and
    // a lawyer has been pulled out of the pool for a call they cannot take.
    if (await requestMedia()) {
      setCheckoutStep(1);
      setShowCheckout(true);
    }
  };

  // Ask for the camera and mic. Returns true once we have them; otherwise puts
  // the modal up (which can ask again) and returns false. The modal is shown
  // rather than a toast because a toast cannot re-raise the browser's popup,
  // and cannot tell someone who dismissed it from someone who blocked us.
  const requestMedia = async () => {
    setCheckingMedia(true);
    const media = await ensureMediaPermission();
    setCheckingMedia(false);
    if (media.ok) {
      setMediaPrompt(null);
      return true;
    }
    setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState() });
    return false;
  };

  // "Allow access" / "try again" from the modal — carry on into checkout on success.
  const retryMedia = async () => {
    const ok = await requestMedia();
    if (ok) setShowCheckout(true);
    return ok;
  };

  // ---- Create payment intent (authorize-only / manual capture) ------------
  const initCard = async () => {
    if (!authToken || !selectedPackage) return;
    setPayLoading(true);
    try {
      const payData = new FormData();
      payData.append("amount", selectedPackage.price);
      payData.append("call_package_id", selectedPackage.id);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/consultationPayment`,
        payData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Failed to start payment.");
        return;
      }
      clientSecretRef.current = res.data.data.paymentIntent;
      publishableKeyRef.current = res.data.data.publishableKey;
      // Render the card box first; the actual Stripe element is mounted in the
      // effect below once #cc-card-element exists in the DOM.
      setCardReady(true);
    } catch (e) {
      toast.error("An error occurred while preparing payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // Auto-prepare the Stripe payment intent as soon as step 3 opens for a
  // paid package, so the card box is ready without an extra click.
  useEffect(() => {
    if (checkoutStep !== 3 || !selectedPackage) return;
    const free = !!selectedPackage.is_free || Number(selectedPackage.price) === 0;
    if (!free && !cardReady && !payLoading) initCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStep, selectedPackage]);

  // Mount the Stripe card element once the card box has been rendered.
  // The effect owns the element's whole lifecycle: it mounts a fresh card when
  // cardReady flips on and destroys it on cleanup (close/reopen or StrictMode's
  // mount→cleanup→mount). We intentionally do NOT guard on cardRef.current — a
  // stale ref surviving a previous cycle must not block a re-mount, otherwise
  // reopening the modal leaves #cc-card-element empty.
  useEffect(() => {
    if (!cardReady || !publishableKeyRef.current) return;
    let cancelled = false;
    let card = null;
    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        stripeRef.current = Stripe(publishableKeyRef.current);
        const elements = stripeRef.current.elements();
        card = elements.create("card", { hidePostalCode: true });
        card.mount("#cc-card-element");
        cardRef.current = card;
      } catch (e) {
        toast.error("Failed to load the card form. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
      if (card) {
        try { card.destroy(); } catch (e) { /* already gone */ }
      }
      if (cardRef.current === card) cardRef.current = null;
    };
  }, [cardReady]);

  // ---- Book the call + kick off the lawyer search --------------------------
  // Shared by the paid flow (after Stripe authorizes the card) and the free
  // flow (package price is 0, so there's nothing to authorize).
  const bookCallAndSearch = async (transactionId, paymentStatus) => {
    const callData = new FormData();
    callData.append("call_package_id", selectedPackage.id);
    callData.append("category_id", categoryId);
    callData.append("jurisdiction_id", jurisdictionId);
    callData.append("case_description", caseDescription);
    callData.append("amount_paid", selectedPackage.price);
    callData.append("currency", selectedPackage.currency || "USD");
    callData.append("transaction_id", transactionId);
    callData.append("payment_status", paymentStatus);

    const callRes = await axios.post(
      `${process.env.REACT_APP_API_URL}/storeCallHistory`,
      callData,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    if (callRes?.data?.status) {
      const callId = callRes.data.data?.call_id;
      setShowCheckout(false);
      resetCheckout();
      // Show the "finding a lawyer" screen right away so there's no blank
      // gap while initiateConsultation (2-3s) is in flight. It starts in the
      // "finding…" loading state and flips to "lawyers found" once the request
      // returns with the matched lawyers.
      setLawyersFound(false);
      setCallPhase("searching");
      phaseRef.current = "searching";
      // Start the give-up timer here rather than after initiateConsultation
      // resolves, so it expires exactly when the on-screen countdown hits 0.
      clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(handleSearchTimeout, SEARCH_TIMEOUT_MS);
      await startSearching(callId);
    } else {
      toast.error(callRes?.data?.message || "Booking failed. Please contact support.");
    }
  };

  // ---- Pay + initiate call ------------------------------------------------
  const handlePay = async () => {
    if (!stripeRef.current || !cardRef.current || !clientSecretRef.current) {
      toast.error("Payment form is not ready yet.");
      return;
    }
    setPayLoading(true);
    try {
      const result = await stripeRef.current.confirmCardPayment(clientSecretRef.current, {
        payment_method: { card: cardRef.current, billing_details: { name: fullName, email } },
      });
      if (result.error) {
        toast.error(result.error.message || "Payment failed.");
        return;
      }
      // With manual capture the card is authorized (held) but not charged, so
      // the intent settles in "requires_capture". "succeeded" is only returned
      // if the backend is using automatic capture. Accept both as success.
      const intentStatus = result.paymentIntent?.status;
      if (intentStatus !== "requires_capture" && intentStatus !== "succeeded") {
        toast.error("Payment could not be authorized.");
        return;
      }

      await bookCallAndSearch(
        result.paymentIntent.id,
        intentStatus === "requires_capture" ? "authorized" : "paid"
      );
    } catch (e) {
      toast.error("An error occurred while processing your payment.");
    } finally {
      setPayLoading(false);
    }
  };

  // Free package (price 0): skip Stripe/the card sheet entirely and book the
  // call directly.
  const handleFreeCall = async () => {
    if (!selectedPackage) return;
    setPayLoading(true);
    try {
      await bookCallAndSearch("", "free");
    } catch (e) {
      toast.error("An error occurred while booking your call.");
    } finally {
      setPayLoading(false);
    }
  };

  // ---- Broadcast the request to matched lawyers and wait for an accept ------
  // Matches the countdown FindingLawyer shows as the estimated connection time.
  const SEARCH_TIMEOUT_MS = SEARCH_SECONDS * 1000;

  const startSearching = async (callId) => {
    try {
      const reqData = new FormData();
      reqData.append("call_id", callId);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/initiateConsultation`,
        reqData,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not start the search. Please contact support.");
        resetCall();
        return;
      }
      const d = res.data.data;
      const lawyers = d.lawyers || [];
      setMatchedLawyers(lawyers);
      matchedRef.current = lawyers;
      setAvailableCount(d.available ?? lawyers.length);
      setLawyersFound(true); // flip the search screen from "finding…" to "found"

      const call = {
        callId: d.callId,
        channel: d.channel,
        creds: { appId: d.agoraAppId, channel: d.channel, token: d.token, uid: d.uid },
        lawyer: null,
        user: d.user,
        // Paid plan length in minutes — drives the in-call countdown/auto-end.
        durationMinutes: Number(selectedPackage?.duration_minutes) || 0,
        // The OTP-verified token so the in-call chat can call the persisted
        // chat APIs (getChatMessages / sendMessage) on the user's behalf.
        authToken: getAuthToken(),
        // Snapshot of what the user entered on the form, shown during the call.
        // Captured here so it survives the checkout/form reset after payment.
        caseDetails: {
          category: categoriesList.find((c) => String(c.id) === String(categoryId))?.name || "",
          jurisdiction: jurisdictionsList.find((j) => String(j.id) === String(jurisdictionId))?.name || "",
          description: caseDescription,
          plan: selectedPackage
            ? `${selectedPackage.duration_minutes} Minutes${selectedPackage.title ? ` · ${selectedPackage.title}` : ""}`
            : "",
        },
      };
      callRef.current = call;
      setActiveCall(call);
      setCallPhase("searching");

      // Register this socket as the caller and broadcast to matched lawyers.
      SocketService.emit("user-connected", { id: d.user?.id, name: d.user?.name });
      SocketService.emit("consultation-initiate", {
        callId: d.callId,
        channel: d.channel,
        lawyer_ids: lawyers.map((l) => l.id),
        user: d.user,
      });
    } catch (e) {
      toast.error("An error occurred while finding a lawyer.");
      resetCall();
    }
  };

  // Join whoever just accepted — shared by the normal `call-accepted` socket
  // event and by losing the cancel/timeout race below (the server's ack
  // tells us a lawyer claimed it right as we tried to call it off).
  const applyAccepted = (lawyerId, payloadLawyer) => {
    clearTimeout(searchTimer.current);
    const c = callRef.current;
    if (!c) return;
    const fromList = (matchedRef.current || []).find((l) => String(l.id) === String(lawyerId));
    const lawyer = fromList || payloadLawyer ? { ...(payloadLawyer || {}), ...(fromList || {}) } : null;
    const next = { ...c, lawyer };
    callRef.current = next;
    setActiveCall(next);
    setCallPhase("connected");
    phaseRef.current = "connected";
  };

  // Stop broadcasting and release the authorization (the card is never charged
  // for a call that did not connect) — but a lawyer's accept can claim the
  // call microseconds before this lands server-side. Wait for the ack rather
  // than tearing down (and dropping the auth token) optimistically: if we
  // lost that race, the lawyer already got `call-accepted` and is joining
  // the channel, so we join it too instead of leaving them alone and losing
  // the token the now-live call still needs.
  const releaseSearch = (c, onCancelled) => {
    clearTimeout(searchTimer.current);
    if (!c) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      onCancelled();
      return;
    }
    let settled = false;
    const stillOurs = () => callRef.current?.callId === c.callId;
    const teardown = () => {
      axios.post(
        `${process.env.REACT_APP_API_URL}/endCall`,
        (() => { const f = new FormData(); f.append("callId", c.callId); return f; })(),
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      ).catch(() => {}).finally(() => localStorage.removeItem(AUTH_TOKEN_KEY));
      onCancelled();
    };

    SocketService.emit("consultation-cancel", { callId: c.callId }, (ack) => {
      if (settled) return;
      settled = true;
      if (ack?.status === "already-accepted") {
        if (phaseRef.current === "searching" && stillOurs()) applyAccepted(ack.lawyer_id, null);
        return;
      }
      if (phaseRef.current === "searching" && stillOurs()) teardown();
    });
    // No ack within a few seconds (server hiccup, dropped connection) —
    // don't strand the client on the search screen forever.
    setTimeout(() => {
      if (settled) return;
      settled = true;
      if (phaseRef.current === "searching" && stillOurs()) teardown();
    }, 4000);
  };

  // Cancel while still searching: back to the form.
  const cancelSearch = () => {
    const c = callRef.current;
    if (!c) { resetCall(); return; }
    releaseSearch(c, resetCall);
  };

  // The estimated connection time ran out with no lawyer accepting: stop the
  // search and tell the user everyone is busy.
  const handleSearchTimeout = () => {
    if (phaseRef.current !== "searching") return;
    const c = callRef.current;
    if (!c) {
      resetCall();
      setCallPhase("unavailable");
      phaseRef.current = "unavailable";
      return;
    }
    releaseSearch(c, () => {
      resetCall();
      setCallPhase("unavailable");
      phaseRef.current = "unavailable";
    });
  };

  const dismissUnavailable = () => {
    setCallPhase("idle");
    phaseRef.current = "idle";
  };

  // Hang up an active call: backend captures the held payment.
  const endActiveCall = () => {
    const c = callRef.current;
    if (c) {
      SocketService.emit("call-end", { callId: c.callId });
      axios.post(
        `${process.env.REACT_APP_API_URL}/endCall`,
        (() => { const f = new FormData(); f.append("callId", c.callId); return f; })(),
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      ).catch(() => {}).finally(() => localStorage.removeItem(AUTH_TOKEN_KEY));
    } else {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
    resetCall();
  };

  const resetCall = () => {
    clearTimeout(searchTimer.current);
    callRef.current = null;
    matchedRef.current = [];
    setActiveCall(null);
    setCallPhase("idle");
    setMatchedLawyers([]);
    setLawyersFound(false);
  };

  // Socket listeners for the whole call lifecycle.
  useEffect(() => {
    const unsubs = [
      SocketService.subscribe("call-accepted", (payload) => {
        if (phaseRef.current !== "searching") return;
        applyAccepted(payload?.lawyer_id, payload?.lawyer);
      }),
      SocketService.subscribe("call-ended", () => {
        if (phaseRef.current === "connected") {
          // The lawyer hung up. Report it before the token goes, or nothing
          // ever closes this call: it would sit `accepted`, holding the lawyer
          // busy and leaving the authorized card uncaptured. The backend
          // settles on the first report, so racing the lawyer's is harmless.
          const c = callRef.current;
          if (c) {
            const token = getAuthToken();
            axios.post(
              `${process.env.REACT_APP_API_URL}/endCall`,
              (() => { const f = new FormData(); f.append("callId", c.callId); return f; })(),
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {}).finally(() => localStorage.removeItem(AUTH_TOKEN_KEY));
          } else {
            localStorage.removeItem(AUTH_TOKEN_KEY);
          }
          toast.info("The call has ended.");
          resetCall();
        }
      }),
    ];
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear down all checkout/payment state so the next open starts clean. Setting
  // cardReady false triggers the mount effect's cleanup, which destroys the card
  // element; we still null the refs here in case the card never mounted.
  const resetCheckout = () => {
    setCardReady(false);
    setOtp("");
    setOtpVerified(false);
    setAuthToken(null);
    setCheckoutStep(1);
    setPackages([]);
    setSelectedPackage(null);
    setIsFreeTaken(false);
    if (cardRef.current) {
      try { cardRef.current.destroy(); } catch (e) { /* already gone */ }
    }
    stripeRef.current = null;
    cardRef.current = null;
    clientSecretRef.current = null;
    publishableKeyRef.current = null;
  };

  // Step 3 -> Step 2: tear down any in-progress card/payment-intent state so
  // initCard cleanly re-runs if the user comes back to step 3 (e.g. after
  // switching to a different package).
  const backToPackages = () => {
    setCardReady(false);
    if (cardRef.current) {
      try { cardRef.current.destroy(); } catch (e) { /* already gone */ }
    }
    stripeRef.current = null;
    cardRef.current = null;
    clientSecretRef.current = null;
    publishableKeyRef.current = null;
    setCheckoutStep(2);
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    resetCheckout();
    // Abandoning the modal before starting a call — drop the persisted token.
    localStorage.removeItem(AUTH_TOKEN_KEY);
  };

  // Prefer the backend's explicit is_free flag; fall back to price === 0 for
  // packages that predate that field.
  const isPackageFree = (p) => !!p && (!!p.is_free || Number(p.price) === 0);
  const fmtPrice = (p) => (isPackageFree(p) ? "Free" : `${p.currency || "USD"} ${Number(p.price).toFixed(2)}`);
  const isFreePackage = isPackageFree(selectedPackage);

  return (
    <div className="cc-page font-inter">
      <Seo
        title="Consultation Call | Talk to a Lawyer on Video - Legal Platform"
        description="Connect with a verified lawyer on a video call within minutes. Choose your category, jurisdiction and call duration to get instant legal assistance."
        canonicalPath={process.env.REACT_APP_BASE_PATH + "/consultation-call"}
        image="/favicon.png"
      />
      <Header />

      <main className="cc-main container py-5">
        <div className="cc-hero-grid">
          {/* ----------------------- LEFT: FORM ----------------------- */}
          <div className="cc-form-card">
            <div className="text-center">
              <div className="cc-badge-pill">
                <i className="bi bi-lightning-charge-fill" /> INSTANT LEGAL ASSISTANCE
              </div>
              <h1 className="cc-title">Connect with a lawyer in Minutes</h1>
              <p className="cc-subtitle">
                Fill in the details below and we will connect you with an available lawyer right away.
              </p>
            </div>

            <div className="cc-field">
              <label className="cc-label">Legal Category</label>
              <select className="form-select cc-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">Select legal category</option>
                {categoriesList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Jurisdiction</label>
              <select className="form-select cc-select" value={jurisdictionId} onChange={(e) => setJurisdictionId(e.target.value)}>
                <option value="">Select jurisdiction</option>
                {jurisdictionsList.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </div>

            <div className="cc-field">
              <label className="cc-label">Explain Your Issue</label>
              <textarea
                className="form-control cc-textarea"
                placeholder="Please describe your legal in detail..."
                maxLength={500}
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
              />
              {caseDescription.trim().split(/\s+/).filter(Boolean).length >= 2 && (
                <div className="cc-ai-actions">
                  <button
                    type="button"
                    className="cc-ai-btn"
                    onClick={handleAiAssist}
                    disabled={aiLoading}
                  >
                    <i className="bi bi-stars me-1" />
                    {aiLoading ? "Writing…" : "Write with AI"}
                  </button>
                  {previousDescriptionRef.current && previousDescriptionRef.current !== caseDescription && (
                    <button type="button" className="cc-ai-undo" onClick={handleUndoAiAssist} disabled={aiLoading}>
                      Undo
                    </button>
                  )}
                </div>
              )}
            </div>

            <button type="button" className="cc-cta-btn" onClick={handleStartConnecting} disabled={checkingMedia}>
              <i className="bi bi-camera-video-fill me-2" />
              {checkingMedia ? "Checking camera & mic…" : "Start Connecting with Available Lawyer"}
            </button>
            <div className="cc-secure-note">
              <i className="bi bi-lock-fill" /> Your information is secure and encrypted
            </div>
          </div>

          {/* ----------------------- RIGHT: HERO ----------------------- */}
          <div className="cc-hero-visual">
            <img src={lawyerImg} alt="Verified lawyer ready to help" className="cc-hero-img" />

            <div className="cc-float cc-float-online">
              <span className="cc-online-dot" />
              <div>
                <div className="cc-float-strong">42 <span>Lawyers Online</span></div>
                <div className="cc-float-sub">Ready to help you</div>
              </div>
            </div>

            <div className="cc-float cc-float-stats">
              <div className="cc-stat">
                <i className="bi bi-clock-history" />
                <div>
                  <div className="cc-stat-label">Average Connection Time</div>
                  <div className="cc-stat-strong">32 Seconds</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-shield-check" />
                <div>
                  <div className="cc-stat-label">100% Confidential</div>
                  <div className="cc-stat-sub">Your privacy is our priority</div>
                </div>
              </div>
              <div className="cc-stat">
                <i className="bi bi-camera-video" />
                <div>
                  <div className="cc-stat-label">Secure Video Calls</div>
                  <div className="cc-stat-sub">End-to-end encrypted</div>
                </div>
              </div>
            </div>

            <div className="cc-float cc-float-sos">
              <div className="cc-sos-head"><i className="bi bi-exclamation-octagon-fill" /> SOS</div>
              <div className="cc-sos-title">Emergency Call</div>
              <div className="cc-sos-text">Connect instantly with a lawyer in emergency.</div>
              <button type="button" className="cc-sos-btn" onClick={handleStartConnecting} disabled={checkingMedia}>
                <i className="bi bi-telephone-fill me-1" /> {checkingMedia ? "Checking…" : "Call Now"}
              </button>
            </div>
          </div>
        </div>

        {/* ----------------------- HOW IT WORKS ----------------------- */}
        <div className="cc-how">
          <div className="cc-how-title">HOW IT WORKS</div>
          <div className="cc-how-grid">
            {HOW_IT_WORKS.map((s, i) => (
              <React.Fragment key={s.title}>
                <div className="cc-how-item">
                  <div className="cc-how-icon">
                    <i className={`bi ${s.icon}`} />
                    <span className="cc-how-num">{i + 1}</span>
                  </div>
                  <div className="cc-how-name">{s.title}</div>
                  <div className="cc-how-text">{s.text}</div>
                </div>
                {i < HOW_IT_WORKS.length - 1 && <div className="cc-how-arrow"><i className="bi bi-arrow-right" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </main>

      {/* ------------------ CAMERA / MIC PERMISSION ------------------- */}
      {mediaPrompt && (
        <MediaPermissionModal
          reason={mediaPrompt.reason}
          state={mediaPrompt.state}
          onRetry={retryMedia}
          onCancel={() => setMediaPrompt(null)}
        />
      )}

      {/* ----------------------- CHECKOUT MODAL ----------------------- */}
      {showCheckout && (
        <div className="cc-modal-overlay" onClick={closeCheckout}>
          <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cc-modal-close" onClick={closeCheckout} aria-label="Close">
              <i className="bi bi-x-lg" />
            </button>

            <div className="cc-steps">
              {[
                { n: 1, label: "Verify" },
                { n: 2, label: "Package" },
                { n: 3, label: "Payment" },
              ].map(({ n, label }) => (
                <div key={n} className={`cc-step ${checkoutStep === n ? "active" : ""} ${checkoutStep > n ? "done" : ""}`}>
                  <span className="cc-step-num">{checkoutStep > n ? <i className="bi bi-check-lg" /> : n}</span>
                  <span className="cc-step-label">{label}</span>
                </div>
              ))}
            </div>

            {/* ---- Step 1: Verify identity ---- */}
            {checkoutStep === 1 && (
              <>
                <h3 className="cc-modal-title">Verify Your Identity</h3>
                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <input type="text" className="form-control cc-input" placeholder="Full Name"
                      value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={otpVerified} />
                  </div>
                  <div className="col-md-6">
                    <input type="email" className="form-control cc-input" placeholder="Email Address"
                      value={email} onChange={(e) => setEmail(e.target.value)} disabled={otpVerified} />
                  </div>
                </div>

                {!otpVerified ? (
                  <div className="postq-otp-block mt-2">
                    <button type="button" className="postq-otp-btn w-100" onClick={handleSendOtp} disabled={otpLoading}>
                      {otpLoading ? "Sending..." : "Send OTP"}
                    </button>
                    <input type="text" className="postq-otp-input" placeholder="Enter OTP" value={otp}
                      onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setOtp(e.target.value); }} maxLength="4" />
                  </div>
                ) : (
                  <div className="cc-verified mt-2"><i className="bi bi-patch-check-fill" /> Email verified</div>
                )}

                <button
                  type="button"
                  className="cc-cta-btn mt-3"
                  onClick={() => setCheckoutStep(2)}
                  disabled={!otpVerified || packagesLoading}
                >
                  {packagesLoading ? "Loading packages…" : "Continue"}
                </button>
              </>
            )}

            {/* ---- Step 2: Choose a package ---- */}
            {checkoutStep === 2 && (
              <>
                <button type="button" className="cc-step-back mb-2" onClick={() => setCheckoutStep(1)}>
                  <i className="bi bi-arrow-left" /> Back
                </button>
                <h3 className="cc-modal-title">Choose Call Duration</h3>
                {packagesLoading ? (
                  <div className="cc-secure-note">Loading packages…</div>
                ) : (
                  <div className="cc-duration-grid">
                    {packages.map((p) => {
                      const free = isPackageFree(p);
                      // Free package(s) disable once the free trial is used; paid
                      // packages disable until it is, so it gets used first.
                      const disabled = isFreeTaken ? free : !free;
                      return (
                        <button
                          type="button"
                          key={p.id}
                          className={`cc-duration-card ${selectedPackage?.id === p.id ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                          onClick={() => setSelectedPackage(p)}
                          disabled={disabled}
                        >
                          <i className="bi bi-clock cc-duration-icon" />
                          <span className="cc-duration-title">{p.duration_minutes} Minutes</span>
                          <span className="cc-duration-price">{fmtPrice(p)}</span>
                          {disabled ? (
                            <span className="cc-duration-pop cc-duration-pop-muted">
                              {free ? "Already Used" : "Use Free First"}
                            </span>
                          ) : (
                            p.is_popular && <span className="cc-duration-pop">Popular</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="cc-cta-btn mt-3" onClick={() => setCheckoutStep(3)} disabled={!selectedPackage}>
                  Continue
                </button>
              </>
            )}

            {/* ---- Step 3: Payment / start call ---- */}
            {checkoutStep === 3 && (
              <>
                <button type="button" className="cc-step-back mb-2" onClick={backToPackages}>
                  <i className="bi bi-arrow-left" /> Change duration
                </button>
                <h3 className="cc-modal-title">Confirm &amp; Pay</h3>
                <div className="cc-summary">
                  <div className="cc-summary-row">
                    <span>Consultation</span>
                    <span>{selectedPackage?.title} ({selectedPackage?.duration_minutes} min)</span>
                  </div>
                  <div className="cc-summary-row cc-summary-total">
                    <span>Total</span>
                    <span>{selectedPackage ? fmtPrice(selectedPackage) : "-"}</span>
                  </div>
                </div>

                {isFreePackage ? (
                  <button type="button" className="cc-cta-btn mt-3" onClick={handleFreeCall} disabled={payLoading}>
                    <i className="bi bi-camera-video-fill me-2" />
                    {payLoading ? "Starting..." : "Start Call — Free"}
                  </button>
                ) : (
                  <>
                    <div className="cc-modal-sub mt-3">Payment</div>
                    {!cardReady ? (
                      <div className="cc-secure-note">Preparing payment…</div>
                    ) : (
                      <>
                        <div id="cc-card-element" className="cc-card-box" />
                        <button type="button" className="cc-cta-btn mt-3" onClick={handlePay} disabled={payLoading}>
                          <i className="bi bi-camera-video-fill me-2" />
                          {payLoading ? "Processing..." : `Authorize & Initiate Call — ${selectedPackage ? fmtPrice(selectedPackage) : ""}`}
                        </button>
                        <div className="cc-secure-note">
                          <i className="bi bi-shield-lock-fill" /> Your card is only held now — charged after your call ends.
                        </div>
                      </>
                    )}
                    <div className="cc-secure-note"><i className="bi bi-lock-fill" /> Secured by Stripe</div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Searching for a lawyer (after payment is authorized), then the
          "all lawyers are busy" screen if nobody accepted in time. */}
      {(callPhase === "searching" || callPhase === "unavailable") && (
        <FindingLawyer
          lawyers={matchedLawyers}
          available={availableCount}
          found={lawyersFound}
          timedOut={callPhase === "unavailable"}
          onCancel={cancelSearch}
          onClose={dismissUnavailable}
        />
      )}

      {/* Live video call once a lawyer accepts */}
      {callPhase === "connected" && activeCall && (
        <CallScreen call={activeCall} onEnd={endActiveCall} />
      )}

      <Footer />
    </div>
  );
};

export default ConsultationCall;
